<script>
const LIST_UNRENDER_DELAY = 350;

export default {
  name: "OrgSwitcher",
  props: {
    orgs: {
      type: Array,
      default: () => []
    },
    currentOrgId: {
      type: [String, Number],
      default: null
    },
    open: {
      type: Boolean,
      default: false
    }
  },
  emits: ["select", "close"],
  data() {
    return {
      query: "",
      canScrollUp: false,
      canScrollDown: false,
      isListRendered: false
    };
  },
  computed: {
    visibleOrgs() {
      return this.isListRendered ? this.filteredOrgs : [];
    },
    searchIcon() {
      return require("../../assets/icons/search.svg");
    },
    filteredOrgs() {
      const query = this.query.trim().toLowerCase();

      const matching = !query
        ? this.orgs
        : this.orgs.filter((org) => {
            const name = (org.name || "").toLowerCase();
            return name.includes(query) || String(org.orgId || "").includes(query);
          });

      // L'organisation courante remonte toujours en tete de liste.
      const currentIndex = matching.findIndex(
        (org) => String(org.orgId) === String(this.currentOrgId)
      );

      if (currentIndex <= 0) {
        return matching;
      }

      const reordered = [...matching];
      const [current] = reordered.splice(currentIndex, 1);
      reordered.unshift(current);

      return reordered;
    }
  },
  methods: {
    displayName(org) {
      const name = org.name || "";
      const suffix = ` - ${org.orgId}`;

      return name.endsWith(suffix) ? name.slice(0, -suffix.length) : name;
    },
    updateScrollShadows() {
      const list = this.$refs.list;

      if (!list) {
        this.canScrollUp = false;
        this.canScrollDown = false;
        return;
      }

      const maxScroll = list.scrollHeight - list.clientHeight;

      this.canScrollUp = list.scrollTop > 1;
      this.canScrollDown = maxScroll > 1 && list.scrollTop < maxScroll - 1;
    },
    onSubmit() {
      const typedOrgId = this.query.trim();

      if (!typedOrgId) {
        return;
      }

      const match = this.orgs.find((org) => String(org.orgId) === typedOrgId);

      if (!match) {
        return;
      }

      this.onSelect(match);
    },
    onSelect(org) {
      if (String(org.orgId) === String(this.currentOrgId)) {
        return;
      }

      this.$emit("select", org.orgId);
    }
  },
  watch: {
    open(isOpen) {
      clearTimeout(this.unrenderTimer);
      if (!isOpen) {
        this.query = "";
        this.unrenderTimer = setTimeout(() => {
          this.isListRendered = false;
        }, LIST_UNRENDER_DELAY);
        return;
      }

      this.isListRendered = true;

      this.$nextTick(() => {
        this.$refs.search?.focus({ preventScroll: true });
        this.updateScrollShadows();
      });
    },
    filteredOrgs() {
      this.$nextTick(() => this.updateScrollShadows());
    }
  },
  mounted() {
    this.$nextTick(() => this.updateScrollShadows());
  },
  beforeUnmount() {
    clearTimeout(this.unrenderTimer);
  }
}
</script>

<template>
  <div class="org-switcher" :class="{ 'is-open': open }">
    <div class="org-switcher-inner">
      <div class="org-switcher-content">
        <div class="org-search">
          <inline-svg class="org-search-icon" :src="searchIcon" />
          <input
            ref="search"
            v-model="query"
            type="text"
            class="org-search-input"
            :placeholder="$t('navBar.searchOrg')"
            @keydown.enter.prevent="onSubmit"
            @keydown.esc.stop="$emit('close')"
          />
        </div>

        <div class="org-list-wrap">
          <div class="org-scroll-shadow top" :class="{ 'is-visible': canScrollUp }"></div>
          <div class="org-scroll-shadow bottom" :class="{ 'is-visible': canScrollDown }"></div>

          <div ref="list" class="org-list" @scroll="updateScrollShadows">
            <div
              v-for="org in visibleOrgs"
              :key="org.orgId"
              class="org-row"
              :class="{ 'is-current': String(org.orgId) === String(currentOrgId) }"
              @click="onSelect(org)"
            >
              <span class="org-avatar">{{ org.orgId }}</span>
              <span class="org-info">
                <span class="org-name">{{ displayName(org) }}</span>
                <span class="org-id">{{ org.orgId }}</span>
              </span>
            </div>

            <p v-if="isListRendered && !filteredOrgs.length" class="org-empty">
              {{ $t('navBar.noOrgFound') }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.org-switcher {
  display: grid;
  grid-template-rows: 0fr;
  transition: grid-template-rows 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;

  &.is-open {
    grid-template-rows: 1fr;
  }
}

.org-switcher-inner {
  overflow: hidden;
  min-height: 0;
}

.org-switcher-content {
  width: 0;
  min-width: 100%;
  padding: 0 18px 12px 18px;
  opacity: 0;
  transition: opacity 0.18s ease !important;

  .is-open & {
    opacity: 1;
    transition-delay: 0.08s !important;
  }
}

.org-search {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 40px;
  padding: 0 12px;
  border-radius: $radius-small;
  background-color: $color-input-back;
}

.org-search-icon {
  width: 14px;
  height: 14px;
  min-width: 14px;
  flex: 0 0 14px;
  color: $color-secondary-text;
  stroke: $color-secondary-text;
}

.org-search-input {
  flex: 1 1 auto;
  min-width: 0;
  height: 100%;
  padding: 0;
  border: none;
  outline: none;
  background: transparent;
  color: $color-white-text;
  font-size: 13px;

  &::placeholder {
    color: $color-secondary-text;
  }
}

.org-list-wrap {
  position: relative;
  margin-top: 10px;
}

.org-scroll-shadow {
  position: absolute;
  left: 1px;
  right: 1px;
  height: 26px;
  z-index: 1;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.22s ease !important;

  &.is-visible {
    opacity: 1;
  }

  &.top {
    top: 1px;
    border-radius: $radius-small $radius-small 0 0;
    background: linear-gradient(to bottom, $color-card-back, rgba(28, 32, 41, 0));
  }

  &.bottom {
    bottom: 1px;
    border-radius: 0 0 $radius-small $radius-small;
    background: linear-gradient(to top, $color-card-back, rgba(28, 32, 41, 0));
  }
}

.org-list {
  display: flex;
  flex-direction: column;
  gap: 2px;
  max-height: 208px;
  overflow-y: auto;
  padding: 6px;
  border: 1px solid $color-separator;
  border-radius: $radius-small;
}

.org-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px;
  border-radius: $radius-small;
  cursor: pointer;
  transition: background-color 0.25s ease !important;

  &:hover {
    background-color: $color-input-back;
  }

  &.is-current {
    background-color: $color-input-back-darker;
    cursor: default;

    &:hover {
      background-color: $color-input-back-darker;
    }

    .org-name {
      color: $color-primary-blue;
    }
  }
}

.org-avatar {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  min-width: 28px;
  flex: 0 0 28px;
  border-radius: 6px;
  background-color: $color-primary-blue;
  color: $color-white-text;
  font-size: 11px;
  font-weight: 500;
}

.org-info {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.org-name {
  font-size: 13px;
  color: $color-white-text;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.org-id {
  font-size: 11px;
  color: $color-secondary-text;
}

.org-empty {
  margin: 8px 0 0 !important;
  padding: 0 8px;
  font-size: 12px;
  color: $color-secondary-text;
}
</style>
